/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * CENTRALIZED OPENAI CLIENT & RETRY LOGIC
 * ═══════════════════════════════════════════════════════════════════════════════
 * Shared OpenAI helpers used across all screens.
 * Includes automatic retry with exponential backoff for transient errors
 * (520, 429, 500, 502, 503, 504).
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import OpenAI from 'openai'

const STORAGE_KEY = 'ptp_openai_api_key'

/** Read the saved API key from localStorage */
export function getSavedApiKey(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || ''
  } catch {
    return ''
  }
}

/** Persist an API key to localStorage */
export function saveApiKey(key: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, key)
  } catch {
    // silently ignore storage errors
  }
}

/** Create a raw OpenAI client instance */
export function getOpenAIClient(): OpenAI {
  const apiKey = getSavedApiKey()
  return new OpenAI({
    apiKey: apiKey || 'missing-key',
    dangerouslyAllowBrowser: true,
  })
}

// ── Retry-enabled wrapper ────────────────────────────────────────────────────

/** HTTP status codes that are transient and worth retrying */
const RETRYABLE_STATUS_CODES = new Set([
  429, 500, 502, 503, 504, 520, 521, 522, 524,
])

/** Maximum number of attempts (1 initial + 2 retries) */
const MAX_ATTEMPTS = 3

/** Base delay in ms — doubled on each retry */
const BASE_DELAY_MS = 1000

function isRetryableError(err: unknown): boolean {
  if (err && typeof err === 'object') {
    // OpenAI SDK attaches `status` on API errors
    const status = (err as any).status ?? (err as any).statusCode
    if (typeof status === 'number' && RETRYABLE_STATUS_CODES.has(status)) {
      return true
    }
    // Check the error message for status codes (some wrappers embed it)
    const msg: string = (err as any).message ?? ''
    if (/status\s*code\s*(429|500|502|503|504|520|521|522|524)/i.test(msg)) {
      return true
    }
    // Network-level failures (fetch failed, ECONNRESET, etc.)
    if (/network|fetch|econnreset|socket hang up|timeout/i.test(msg)) {
      return true
    }
  }
  return false
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Detects requests that were blocked client-side (ad blockers, browser
 * extensions, corporate firewalls, VPN filters, Brave Shields, etc.).
 *
 * Signature: a TypeError with "Failed to fetch" and NO HTTP status — the
 * request never reached OpenAI's servers, so no response (and no CORS
 * header) ever came back. This presents as a misleading CORS error in
 * devtools, but the real cause is client-side blocking.
 */
function isClientBlockedError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const status = (err as any).status ?? (err as any).statusCode
  if (typeof status === 'number') return false // real HTTP response = not blocked
  const msg: string = (err as any).message ?? ''
  const name: string = (err as any).name ?? ''
  return (
    /failed to fetch|networkerror|load failed/i.test(msg) ||
    name === 'TypeError'
  )
}

/** Build a user-friendly error for client-blocked requests. */
function buildBlockedRequestError(originalErr: unknown): Error {
  const e = new Error(
    'Unable to reach OpenAI. The request was blocked before it left your browser. ' +
      'This is usually caused by an ad blocker, privacy extension (uBlock, AdGuard, Brave Shields), ' +
      'corporate firewall, or VPN that filters api.openai.com. ' +
      'Please disable your ad blocker for this site, try a different browser, or switch off your VPN, then try again.',
  )
  ;(e as any).cause = originalErr
  ;(e as any).code = 'CLIENT_BLOCKED'
  return e
}

/** Detects 401 Unauthorized — invalid, missing, or revoked API key. */
function isUnauthorizedError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const status = (err as any).status ?? (err as any).statusCode
  return status === 401
}

/** Build a user-friendly error for missing/invalid API keys. */
function buildUnauthorizedError(originalErr: unknown): Error {
  const hasKey = !!getSavedApiKey()
  const message = hasKey
    ? 'Your OpenAI API key was rejected (401 Unauthorized). It may be invalid, expired, revoked, or out of credits. Please reconnect a valid key in the integrations panel.'
    : 'No OpenAI API key found. Please connect your OpenAI API key in the integrations panel before using AI features.'
  const e = new Error(message)
  ;(e as any).cause = originalErr
  ;(e as any).code = 'UNAUTHORIZED'
  return e
}

/**
 * Wrapper around `client.chat.completions.create()` with automatic retry
 * for transient server errors (520, 429, 5xx, network failures).
 *
 * Usage:
 * ```ts
 * const completion = await chatCompletionWithRetry({ model, messages, ... })
 * ```
 */
export async function chatCompletionWithRetry(
  params: OpenAI.Chat.Completions.ChatCompletionCreateParamsNonStreaming,
): Promise<OpenAI.Chat.Completions.ChatCompletion> {
  const client = getOpenAIClient()
  let lastError: unknown

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const result = await client.chat.completions.create(params)
      return result
    } catch (err) {
      lastError = err
      const status = (err as any)?.status
      console.warn(
        `[OpenAI] Attempt ${attempt}/${MAX_ATTEMPTS} failed` +
          (status ? ` (status ${status})` : '') +
          `: ${(err as any)?.message ?? err}`,
      )

      // 401 Unauthorized: don't retry, surface clear message about API key
      if (isUnauthorizedError(err)) {
        console.error('[OpenAI] API key rejected (401). Aborting retries.')
        throw buildUnauthorizedError(err)
      }

      // Client-blocked requests: don't retry, surface clear message immediately
      if (isClientBlockedError(err)) {
        console.error(
          '[OpenAI] Request appears to be blocked client-side (ad blocker, firewall, VPN, or browser extension). ' +
            'Aborting retries.',
        )
        throw buildBlockedRequestError(err)
      }

      if (attempt < MAX_ATTEMPTS && isRetryableError(err)) {
        const delay =
          BASE_DELAY_MS * Math.pow(2, attempt - 1) + Math.random() * 500
        console.log(`[OpenAI] Retrying in ${Math.round(delay)}ms...`)
        await sleep(delay)
        continue
      }

      throw err
    }
  }

  throw lastError
}

/**
 * Convenience: transcribe audio via Whisper with retry.
 */
export async function transcribeWithRetry(
  file: File,
  options?: Partial<OpenAI.Audio.Transcriptions.TranscriptionCreateParams>,
): Promise<OpenAI.Audio.Transcriptions.Transcription> {
  const client = getOpenAIClient()
  let lastError: unknown

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const result = await client.audio.transcriptions.create({
        model: 'whisper-1',
        file,
        ...options,
      })
      return result
    } catch (err) {
      lastError = err
      const status = (err as any)?.status
      console.warn(
        `[OpenAI Whisper] Attempt ${attempt}/${MAX_ATTEMPTS} failed` +
          (status ? ` (status ${status})` : '') +
          `: ${(err as any)?.message ?? err}`,
      )

      // 401 Unauthorized: don't retry, surface clear message about API key
      if (isUnauthorizedError(err)) {
        console.error(
          '[OpenAI Whisper] API key rejected (401). Aborting retries.',
        )
        throw buildUnauthorizedError(err)
      }

      // Client-blocked requests: don't retry, surface clear message immediately
      if (isClientBlockedError(err)) {
        console.error(
          '[OpenAI Whisper] Request appears to be blocked client-side (ad blocker, firewall, VPN, or browser extension). ' +
            'Aborting retries.',
        )
        throw buildBlockedRequestError(err)
      }

      if (attempt < MAX_ATTEMPTS && isRetryableError(err)) {
        const delay =
          BASE_DELAY_MS * Math.pow(2, attempt - 1) + Math.random() * 500
        console.log(`[OpenAI Whisper] Retrying in ${Math.round(delay)}ms...`)
        await sleep(delay)
        continue
      }

      throw err
    }
  }

  throw lastError
}
